import { type ChatMessage, type Content, groupAlbums, type MediaRef } from "@rhema/client";
import type { Format } from "@rhema/format";
import { type OpenAs, openAs } from "./files";

type MediaContent = Extract<Content, { type: "media" }>;

/** One thing the viewer shows: a photo or video of the chat, or a file opened on its own. */
export type ViewerEntry = {
  /** The message it came in. */
  id: string;
  media: MediaRef;
  as: OpenAs;
  caption?: string;
  /** "md" when an edit gave the caption formatting. */
  format?: Format;
  mentions?: string[];
  /** The file this device is still sending. */
  local?: Blob;
  /** "You", or the sender's name. */
  sender: string;
  /** Someone else sent it, so it can be saved to Penielly. */
  received: boolean;
  sentAt?: string;
  chatId?: string;
  /** Its place in the album it was sent in, as the chat shows it. */
  album?: { id: string; index: number; count: number };
};

function entryOf(
  m: ChatMessage,
  c: MediaContent,
  meId: string | undefined,
  nameOf: (id: string | null) => string,
) {
  const edited = m.edited !== null && m.edited !== undefined;
  const caption = edited ? (m.edited as string) : c.caption;
  const mentions = c.mentions;
  const entry: ViewerEntry = {
    id: m.id,
    media: c.media,
    as: c.kind === "video" ? "video" : "photo",
    sender: m.senderId && m.senderId === meId ? "You" : nameOf(m.senderId),
    received: Boolean(m.senderId && m.senderId !== meId),
    sentAt: m.createdAt,
    chatId: m.chatId,
    ...(caption ? { caption } : {}),
    ...(edited && m.editedFormat ? { format: m.editedFormat } : {}),
    ...(mentions?.length ? { mentions } : {}),
    ...(m.localFile ? { local: m.localFile } : {}),
  };
  return entry;
}

function visual(m: ChatMessage): MediaContent | null {
  const c = m.content;
  if (m.deleted || c?.type !== "media" || (c.kind !== "photo" && c.kind !== "video")) return null;
  // A file still waiting to upload with no copy on this device has nothing to show yet.
  if (!c.media.key && !m.localFile) return null;
  return c;
}

/**
 * Every photo and video in the chat, oldest first, with albums in album order, for swiping
 * through in the viewer.
 */
export function viewerEntries(
  messages: readonly ChatMessage[],
  meId: string | undefined,
  nameOf: (id: string | null) => string,
): ViewerEntry[] {
  const out: ViewerEntry[] = [];
  for (const group of groupAlbums(messages)) {
    if (group.kind === "single") {
      const c = visual(group.message);
      if (c) out.push(entryOf(group.message, c, meId, nameOf));
      continue;
    }
    const shown = group.messages.filter((m) => visual(m));
    shown.forEach((m, index) => {
      const entry = entryOf(m, visual(m) as MediaContent, meId, nameOf);
      entry.album = { id: group.id, index, count: shown.length };
      out.push(entry);
    });
  }
  return out;
}

/** A file opened on its own, such as a PDF, or null when Rhema cannot show it. */
export function fileEntry(
  m: ChatMessage,
  meId: string | undefined,
  nameOf: (id: string | null) => string,
): ViewerEntry | null {
  const c = m.content;
  if (m.deleted || c?.type !== "media") return null;
  const as = openAs(c.media.mime);
  if (!as) return null;
  return { ...entryOf(m, c, meId, nameOf), as };
}
